import { useEffect, useRef } from 'react';

import parse from 'html-react-parser';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';
import { useScrollToBottom } from 'use-scroll-to-bottom';

import Button from '../../../../components/buttons/Button';
import { DialogHeader } from '../../../../components/Dialog/DialogUiRedesign/DialogHeader';
import type { WizardChildProps } from '../../../../components/Wizard/types';
import { useMarketingConsents } from '../../../../hooks/useMarketingConsents';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { analytics } from '../../../../services/analytics';
import { useFlag } from '../../../../services/launchDarkly';
import { useStores } from '../../../../stores/useStores';
import { convertCmsContent } from '../../../../utils/convertCmsContent';
import { RedesignContainer } from '../RedesignContainer/RedesignContainer';

import styles from './ResponsibleSocialPlayTermsRedesign.module.scss';

const PAGE_SLUG = 'responsible-social-play';

interface ResponsibleSocialPlayTermsRedesignProps extends WizardChildProps {
  onClose?: () => void;
  wizardProps?: WizardChildProps;
}

export const ResponsibleSocialPlayTermsRedesign = observer(function ResponsibleSocialPlayTermsRedesign(
  props: ResponsibleSocialPlayTermsRedesignProps,
) {
  const isStepSkipped = useRef(false);
  const { isCurrent, prevStep, onClose } = props;

  const { portalStore, cmsStore, liveGeoStore, marketingConsentStore } = useStores();

  const { signUpMarketingConsents, fetchingSignUpMarketingConsent } = marketingConsentStore;
  const { countryCode } = liveGeoStore.rawGeo ?? {};

  const { t } = useTranslation();

  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [setBottomRef, isBottom] = useScrollToBottom();

  const { areAllOptionalConsentsChecked } = useMarketingConsents({
    initialMarketingConsents: signUpMarketingConsents,
    fetchingMarketingConsents: fetchingSignUpMarketingConsent,
    localStorageKey: 'signup-marketing-consents',
  });

  const responsible_gaming_dialog = useFlag('responsible_gaming_dialog', ['']);
  const shouldShowRSGDialog = responsible_gaming_dialog.includes(countryCode || 'US') && !areAllOptionalConsentsChecked;

  useEffect(() => {
    if (!isCurrent || isStepSkipped.current) {
      return;
    }
    if (shouldShowRSGDialog) {
      void analytics.sendEvent('responsible_social_play', {
        event_category: 'sign_up',
        event_action: 'impression',
      });
      return;
    }
    isStepSkipped.current = true;
    portalStore.closePortal();
  }, [isCurrent, shouldShowRSGDialog, portalStore]);

  const { value: cmsContent, loading: cmsContentLoading } = useAsync(async () => {
    const content = await cmsStore.fetchCmsPage(PAGE_SLUG);
    return parse(convertCmsContent(content) || '');
  }, [cmsStore]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      portalStore.closePortal();
    }
  };

  const handleGoBack = () => {
    if (onClose) {
      onClose();
    } else {
      prevStep();
    }
  };

  const handleSubmit = () => {
    void analytics.sendEvent('responsible_social_play', {
      event_category: 'sign_up',
      event_action: 'click',
      button_text: 'accept',
    });
    if (onClose) {
      onClose();
    } else {
      portalStore.closePortal();
    }
  };

  if (!shouldShowRSGDialog) {
    return null;
  }

  return (
    <RedesignContainer className={styles.container}>
      <DialogHeader
        className={styles.dialogHeader}
        closeBtnDataTestId="rsg-close-btn"
        decorationsWrapperClassName={styles.dialogHeaderDecorationsWrapperClassName}
        goBackBtnDataTestId="rsg-back-btn"
        title={
          t('auth.responsibleSocialPlay').charAt(0).toUpperCase() +
          t('auth.responsibleSocialPlay').slice(1).toLowerCase()
        }
        titleClassName={styles.dialogHeaderTitle}
        {...(isDesktop ? { onClose: handleClose } : { onGoBack: handleGoBack })}
      />
      <div className={styles.container}>
        <div className={styles.content}>
          <p className={styles.subtitle}>{t('auth.readTheTermsToProceed')}</p>

          <div className={styles.termsSection}>
            <div className={styles.responsibleDescription}>{cmsContent}</div>
            <div ref={setBottomRef} />
          </div>

          <div className={styles.acceptButtonWrapper}>
            <Button
              btnType="main"
              className={styles.acceptButton}
              dataTestId="rsg-accept-btn"
              disabled={!isBottom || cmsContentLoading}
              onClick={handleSubmit}
            >
              {t('common.buttons.accept')}
            </Button>
          </div>
        </div>
      </div>
    </RedesignContainer>
  );
});
